import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import {
  Table,
  Space,
  Input,
  Form,
  Row,
  Col,
  Image,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { BannerActionListOut } from '@/api/generated/schemas';
import { BannerSize, BannerActionKind } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import {
  BannerActionKindTag,
  BannerSizeTag,
  TargetAvailabilityTag,
} from '@/components/user_catalog/banner_actions/BannerActionTags';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const BannerActionList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<BannerActionListOut>({
      pagination: { pageSize: 20 },
      resource: 'user_catalog.banner_actions',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'user_catalog.banner_actions.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('user_catalog.banner_actions', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="image_url"
          title={t('user_catalog.banner_actions.fields.image_url')}
          render={(value: string) =>
            value ? (
              <Image
                src={value}
                height={60}
                style={{ objectFit: 'cover' }}
                preview={false}
              />
            ) : (
              '-'
            )
          }
        />

        <Table.Column
          dataIndex="name"
          title={t('user_catalog.banner_actions.fields.name')}
        />

        <Table.Column
          dataIndex="size"
          title={t('user_catalog.banner_actions.fields.size')}
          render={(value: BannerSize) => <BannerSizeTag value={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'size')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="size"
              minWidth={140}
              placeholder={t('user_catalog.banner_actions.fields.size')}
              options={[
                {
                  label: t('user_catalog.banner_actions.size.large'),
                  value: BannerSize.large,
                },
                {
                  label: t('user_catalog.banner_actions.size.small'),
                  value: BannerSize.small,
                },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="kind"
          title={t('user_catalog.banner_actions.fields.kind')}
          render={(value: BannerActionKind) => (
            <BannerActionKindTag value={value} />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'kind')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="kind"
              minWidth={180}
              placeholder={t('user_catalog.banner_actions.fields.kind')}
              options={Object.values(BannerActionKind).map((value) => ({
                label: t(`user_catalog.banner_actions.kind.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="target_available"
          title={t('user_catalog.banner_actions.fields.target')}
          render={(value: boolean | null) =>
            value == null ? '-' : <TargetAvailabilityTag value={value} />
          }
        />

        <Table.Column
          dataIndex="deeplink"
          title={t('user_catalog.banner_actions.fields.deeplink')}
          // No copy affordance here — the row click navigates to show,
          // where the copyable version lives.
          render={(value: string) =>
            value ? (
              <Tooltip title={value}>
                <Typography.Text code ellipsis style={{ maxWidth: 200 }}>
                  {value}
                </Typography.Text>
              </Tooltip>
            ) : (
              <Tag color="warning">
                {t('user_catalog.banner_actions.deeplink_none')}
              </Tag>
            )
          }
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: BannerActionListOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
